import { Tag } from 'antd';
import moment from 'moment';
import { connect } from 'react-redux';
import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import cloneDeep from 'lodash.clonedeep';
import ReactEcharts from '../../Echarts/Echarts';
import { config } from '../../constant';
import { pieSingleMockJson } from '../../../Mock/MockJson';
import { ErrorComponent, NoRecordComponent } from '../../Utils';
import { trendLabelTranslation } from '../../../Utils/Adapter/translationAdapter';
import { getChartDataWithoutSelection, getChartDataWithSelection } from '../../ChartCalls';

const PieSingleLine = (props) => {
  const [option, setOption] = useState({});
  const [refreshDateTime, setRefreshDateTime] = useState(moment());
  const [errorInAPI, setErrorInAPI] = useState(false);
  const [noDataInDB, setNoDataInDB] = useState(false);
  const [pieName, setPieName] = useState('');
  let timeTicket = useRef(null);
  let requestCheck = useRef(true);
  const abortController = useRef(new window.AbortController());

  const {
    graphprops,
    selectionDate,
    selectionMonth,
    DeviceCode,
    selectionDateRange,
    dayMonthYear,
    selectedThemeing,
    currentTimeZone,
  } = props;

  useEffect(() => {
    if (timeTicket.current) {
      clearInterval(timeTicket.current);
    }
    let refreshTime = graphprops?.refreshTime ? graphprops.refreshTime * 60000 : config.defaultRefreshTime;
    timeTicket.current = setInterval(() => {
      if (requestCheck.current) {
        getData(props, selectedThemeing);
      }
    }, refreshTime);
    return () => {
      abortController.current.abort();
      clearInterval(timeTicket.current);
    };
  }, []);

  useEffect(() => {
    getData(props);
  }, [
    selectionDate,
    selectionMonth,
    selectionDateRange,
    DeviceCode,
    dayMonthYear
  ]);

  const getData = useCallback(async () => {
    let json = [];
    requestCheck.current = false;
    let { graphprops } = props;
    setErrorInAPI(false);
    setNoDataInDB(false);
    if (graphprops && graphprops.enableMock && graphprops.enableMock === true) {
      json = pieSingleMockJson;
      requestCheck.current = true;
    } else {
      if (graphprops?.graph === 'rangeBar' && graphprops?.currentDateData !== undefined) {
        json = graphprops.currentDateData;
      } else {
        if (
          graphprops &&
          ((graphprops.DashboardLevelSelection === 'datePicker' ||
            graphprops.GraphLevelSelection === 'datePicker') &&
            selectionDate) ||
          (
            (graphprops.DashboardLevelSelection === 'monthPicker' ||
              graphprops.GraphLevelSelection === 'monthPicker') &&
            selectionMonth) ||
          (
            (graphprops.DashboardLevelSelection === 'dateRangePicker' ||
              graphprops.GraphLevelSelection === 'dateRangePicker') &&
            selectionDateRange) ||
          (
            (graphprops.DashboardLevelSelection === 'dateMonthYearPicker' ||
              graphprops.GraphLevelSelection === 'dateMonthYearPicker') &&
            dayMonthYear) ||
          (
            (graphprops.DashboardLevelSelection === 'deviceSelection' ||
              graphprops.GraphLevelSelection === 'deviceSelection') &&
            DeviceCode)
        ) {
          json = await getChartDataWithSelection({
            graphprops,
            selectionDate,
            selectionMonth,
            selectionDateRange,
            DeviceCode,
            dayMonthYear,
            abortController: abortController.current,
            currentTimeZone,
          });
          requestCheck.current = true;
        } else if (
          graphprops.GraphLevelSelection === 'none' ||
          graphprops.DashboardLevelSelection === 'none'
        ) {
          json = await getChartDataWithoutSelection({
            graphprops,
            abortController: abortController.current,
            DeviceCode,
            TimeZone: currentTimeZone,
          });
          requestCheck.current = true;
        }
      }
    }

    if ((json && json.message) || (Array.isArray(json) && json.length === 0 && graphprops.enableMock !== true)) {
      if (json.message) {
        setErrorInAPI(true);
      } else if (json.length === 0) {
        setNoDataInDB(true);
      }
    } else {
      let lineObject = [];
      let pie = json;
      if (pieName) {
        let lineData = json.filter((jsonData) => jsonData.Description === pieName);
        lineObject = lineData && Array.isArray(lineObject) && lineData[0] ? lineData[0].Action : [];
      } else if (pie) {
        lineObject = pie && pie[0] && pie[0].Action ? pie[0].Action : [];
      }

      let yAxis = [
        {
          type: 'value',
          axisLine: {
            lineStyle: {
              color: props.selectedThemeing === 'lightTheme' ? 'black' : 'white'
            }
          },
          axisTick: {
            lineStyle: {
              color: props.selectedThemeing === 'lightTheme' ? 'black' : 'white'
            }
          },
          axisLabel: {
            color: props.selectedThemeing === 'lightTheme' ? 'black' : 'white',
            interval: 0,
            rotate: 30
          }
        }
      ];
      let xAxis = [
        {
          type: 'category',
          data: lineObject && Array.isArray(lineObject) && lineObject.length > 0 ? lineObject.map(item => item.DateTime) : [],
          axisLine: {
            lineStyle: {
              color: props.selectedThemeing === 'lightTheme' ? 'black' : 'white'
            }
          },
          axisTick: {
            lineStyle: {
              color: props.selectedThemeing === 'lightTheme' ? 'black' : 'white'
            }
          },
          axisLabel: {
            color: props.selectedThemeing === 'lightTheme' ? 'black' : 'white'
          }
        }
      ];
      let toolBox = {
        showTitle: false,
        orient: 'vertical',
        itemSize: 25,
        feature: {
          saveAsImage: {
            title: 'download',
            backgroundColor: props.selectedThemeing === 'lightTheme' ? 'white' : 'black',
            name: props.graphprops.title,
            iconStyle: {
              borderColor: '#8c8c8c',
              borderWidth: 2
            }
          }
        }
      };
      let tooltip = {
        trigger: 'item',
        formatter: '{b} : {c} ({d}%)',
        textStyle: {
          fontSize: 15
        }
      };

      let dataZoom = [
        {
          type: 'slider',
          show: true,
          start: 0,
          end: 100,
          backgroundColor: '#9a9191',
          textStyle: {
            color: 'white'
          }
        },
        {
          type: 'inside',
          start: 0,
          end: 100
        }
      ];
      let series = [
        {
          type: 'line',
          stack: 'chart',
          z: 3,
          itemStyle: {
            normal: {
              color: props.graphprops.lineColor ? props.graphprops.lineColor : 'steelblue'
            }
          },
          data: lineObject && Array.isArray(lineObject) && lineObject.length > 0 ? lineObject.map(item => item.Value) : []
        },
        {
          type: 'line',
          silent: true,
          itemStyle: {
            normal: {
              color: props.graphprops.lineColor ? props.graphprops.lineColor : 'red'
            }
          },
          data: lineObject && Array.isArray(lineObject) && lineObject.length > 0 ? lineObject.map(item => item.Value) : []
        },
        {
          type: 'pie',
          radius: '60%',
          center: ['50%', '50%'],
          data: pie && Array.isArray(pie) ? pie.map(pieData => ({
            name: pieData.Description,
            value: pieData.Value
          })) : []
        }
      ];

      series = await trendLabelTranslation(series, props.translationCache);
      const chartOption = cloneDeep(getOption());
      if (props.graphprops.colors && props.graphprops.colors.length > 0) {
        chartOption.color = [...props.graphprops.colors];
      }
      chartOption.series = series;
      chartOption.yAxis = yAxis;
      chartOption.xAxis = xAxis;
      chartOption.dataZoom = dataZoom;
      chartOption.toolbox = toolBox;
      chartOption.tooltip = tooltip;
      setOption(chartOption);
      setRefreshDateTime(moment());
    }
    setErrorInAPI(errorInAPI);
    setNoDataInDB(noDataInDB);
  }, [props, pieName]);

  const getOption = () => {
    let theme = props.selectedThemeing;
    let option = {
      tooltip: {
        trigger: 'item',
        formatter: '{b} : {c} ({d}%)'
      },
      label: {
        color: theme === 'lightTheme' ? 'black' : 'white',
        fontWeight: 'bold',
        fontSize: 14
      },
      textStyle: {
        color: theme === 'lightTheme' ? 'black' : 'white',
        fontWeight: 'bold',
        fontSize: 18
      },
      legend: {
        left: 'center',
        textStyle: {
          color: theme === 'lightTheme' ? 'black' : 'white'
        }
      },
      toolbox: {
        showTitle: false,
        itemSize: 25,
        feature: {
          saveAsImage: {
            title: 'download',
            backgroundColor: theme === 'lightTheme' ? 'white' : 'black',
            name: props.graphprops.title,
            iconStyle: {
              borderColor: '#8c8c8c',
              borderWidth: 2
            }
          }
        }
      },
      grid: { top: '55%' },
      xAxis: [
        {
          type: 'value',
          splitLine: {
            show: false
          },
          axisLine: {
            lineStyle: {
              color: theme === 'lightTheme' ? 'black' : 'white'
            }
          },
          axisTick: {
            lineStyle: {
              color: theme === 'lightTheme' ? 'black' : 'white'
            }
          },
          axisLabel: {
            color: theme === 'lightTheme' ? 'black' : 'white'
          }
        }
      ]
    };
    return option;
  };

  const onChartClick = (cellData) => {
    if (cellData.componentSubType === 'pie') {
      setPieName(cellData.name);
      getData();
    }
  };


  return (
    <Fragment>
      {props.graphprops && props.graphprops.checked === true ? (
        <Tag className="graphTag" color="blue">
          {refreshDateTime ? moment(refreshDateTime).format('YYYY-MM-DD HH:mm:ss') : ''}
        </Tag>
      ) : null}
      {noDataInDB === true || errorInAPI === true ? (
        noDataInDB === true ? (
          <NoRecordComponent />
        ) : (
          <ErrorComponent />
        )
      ) : (
        <ReactEcharts
          option={option}
          notMerge={true}
          lazyUpdate={true}
          onEvents={{ click: onChartClick }}
          style={{ height: '90%', width: '100%' }}
          className="react_for_echarts"
        />
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    translationCache: state.TranslationReducer.translationCache,
    currentTimeZone: state.LayoutReducer.translationCache
  };
};

export default connect(mapStateToProps)(PieSingleLine);